import { isAxiosError } from 'axios';
import { parse, stringify } from 'qs';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { compareTimeRanges, timeItems } from './constant';
import {
    type AutomaticReserveDemandResponseExtended,
    classOfServiceEnumMap,
    type FlightAutoPrices,
} from '.';
import BusPackage from '@itinerary/trip/bus/BusPackage';
import FlightPriceClass from '@itinerary/trip/flight/FlightPriceClass';
import { FlightUrlParams } from '@utils/flight';
import {
    AutomaticReserveApiApiAutomaticReserveAddUserScoreDiscountPost,
    AutomaticReserveApiApiAutomaticReserveCalculateAutomaticReserveDemandItemSuccessRatePost,
    AutomaticReserveApiApiAutomaticReserveCalculateAutomaticReserveRouteDemandItemSuccessRatePost,
    AutomaticReserveApiApiAutomaticReserveCancelAutomaticReserveDemandIdPatch,
    type AutomaticReserveApiApiAutomaticReserveCancelAutomaticReserveDemandIdPatchRequest,
    AutomaticReserveApiApiAutomaticReserveCancellationReasonsGet,
    AutomaticReserveApiApiAutomaticReserveEditAutomaticReserveDemandPost,
    AutomaticReserveApiApiAutomaticReserveGet,
    AutomaticReserveApiApiAutomaticReserveGetAutomaticReserveDetailsByIdGet,
    AutomaticReserveApiApiAutomaticReserveGetAutomicReserveDemandItemsAvailabilityPost,
    AutomaticReserveApiApiAutomaticReserveIdGet,
    AutomaticReserveApiApiAutomaticReserveIdPaymentGet,
    AutomaticReserveApiApiAutomaticReserveIdPeopleDelete,
    type AutomaticReserveApiApiAutomaticReserveIdPeopleDeleteRequest,
    AutomaticReserveApiApiAutomaticReserveIdPeopleV2Get,
    AutomaticReserveApiApiAutomaticReservePost,
    AutomaticReserveApiApiAutomaticReservePriceGet,
    AutomaticReserveApiApiAutomaticReserveRegisterAutomaticReservePeoplePost,
    AutomaticReserveApiApiAutomaticReserveRemoveUserScoreDiscountPost,
    AutomaticReserveApiApiAutomaticReserveSetInProcessAutomaticReserveIdPost,
    type AutomaticReserveCancellationReasonResponse,
    type AutomaticReserveDemandRequest,
    type AutomaticReserveDemandResponse,
    type AutomaticReserveDetailsResponseDto,
    type AutomaticReserveRouteDemandItemRequest,
    AutoReserveTypes,
    BaggageTypes,
    type CreateDemandAutomaticReserveCommandResponse,
    type DepartureTimeRangesDemandDto,
    type EditAutomaticReserveDemandRequestDto,
    type GetAutomaticReservePrice,
    type GetAutomicReserveDemandItemsAvailabilityResponse,
    type RegisterAutomaticReservePeopleRequestDto,
    SeatTypes,
    ServiceType,
} from '@clients/PricetrackApi';
import { PaxType } from '@utils/types';
import { AutoReserveType, ReserveType } from '@utils/itinerary';
import ItineraryPackage from '@itinerary/ItineraryPackage';
import {
    InformationRegisterStatus,
    type ItineraryPackageInvoiceEntries,
} from '@store/invoice';
import type Trip from '@itinerary/trip/Trip';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import type TripPackage from '@itinerary/trip/TripPackage';
import FlightPackage from '@itinerary/trip/flight/FlightPackage';
// eslint-disable-next-line no-restricted-imports
import OptionalPaymentModal from '@pages/reserve/prepay/_components/OptionalPaymentModal.vue';
import ItineraryModule from '@itinerary/ItineraryModule';
import type TripModule from '@itinerary/trip/TripModule';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import type { SelectItemType } from '#imports';

class AutoReserveStore {
    //#region state
    classOfService = classOfServiceEnumMap[0];
    demands: AutomaticReserveDemandResponse[] = [];
    demandsCount = 0;
    prices: Omit<GetAutomaticReservePrice, 'flightPrices'> | null = null;
    flightPrices: FlightAutoPrices[] = [];
    selectedPackages: ItineraryPackage[] = [];
    currentAutoReserveEditing:
        | (AutomaticReserveDetailsResponseDto & {
              id?: number;
          })
        | null = null;

    routeDemand: AutomaticReserveRouteDemandItemRequest = {
        carrierCodes: [],
        vehicleTypeCodes: [],
        baggageType: BaggageTypes.NUMBER_1,
        departureSplitTimesPerDays: [],
    };

    useCredit = true;
    selectedAllItems = false;
    autoReserveSuccessRate = 0;
    selectedSeatType = SeatTypes.NUMBER_1;
    autoReserveType: AutoReserveType = AutoReserveType.None;
    selectedDelay = 30;
    selectedEarly = 30;
    editingInitialLoading = false;
    reserveResponse: CreateDemandAutomaticReserveCommandResponse | null = null;
    isLoadFromSession = false;
    isLoadingCreateReserve = false;
    isLoadingPrices = false;
    preSelectPackage: ItineraryPackage | null = null;
    cancellationReasons: AutomaticReserveCancellationReasonResponse[] = [];
    departureDate = '';
    interceptionThresholdInHour = 0;
    chargeNeededForAutoReserves: number | undefined;
    chargePendingAutoReservesPaymentLink: string | undefined | null;
    originCode = '';
    destinationCode = '';
    currentSelectedFlightPrice: FlightAutoPrices | null = null;
    showAll = false;
    initLoading = false;
    demandAvailabilityCheck: GetAutomicReserveDemandItemsAvailabilityResponse | null =
        null;

    customTimes: {
        to: string | undefined;
        from: string | undefined;
    } | null = null;

    applyUserScoreDiscount = false;
    isUserScoreDiscountOnBillActive = false;
    //#endregion

    get giftMac() {
        return useAuthStore().giftMac;
    }

    get giftCode() {
        return useAuthStore().giftCode;
    }

    get autoReserveNormalizedDemands(): AutomaticReserveDemandResponseExtended[] {
        return this.demands.map(demand => ({
            ...demand,
            packages: this.mapDemandResponseToPackage(demand),
        }));
    }

    mapSelectedPeriods(demands: DepartureTimeRangesDemandDto[]) {
        return timeItems.filter(({ items }) =>
            items
                .map(item => item.value)
                .every(value =>
                    demands?.some(({ timeRanges }) =>
                        timeRanges?.find(it => compareTimeRanges(it, value))
                    )
                )
        );
    }

    mapSelectedCustomPeriods(demands: DepartureTimeRangesDemandDto[]) {
        return timeItems.filter(
            ({ items }) =>
                !items
                    .map(item => item.value)
                    .every(value =>
                        demands?.some(({ timeRanges }) =>
                            timeRanges?.find(it => compareTimeRanges(it, value))
                        )
                    )
        );
    }

    async initAutoReserve() {
        const formStore = useFormStore();
        if (formStore.adultCount) {
            return;
        }
        this.initLoading = true;
        formStore.clearState();
        this.loadSession();
        const searchFormStore = useSearchFormStore();
        const itineraryStore = useItineraryStore();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const currentModule = useCurrentItineraryModule();
        searchFormStore.setReserveType(
            this.reserveType === ReserveType.Bus
                ? ReserveType.Bus
                : ReserveType.Flight
        );
        currentSearchForm.value.loadViewUrlParams({
            destination: this.destinationCode || '',
            origin: this.originCode || '',
            departureDate: new Date(this.departureDate || ''),
            adultCount: formStore.adultCount || 0,
            infantCount: formStore.infantCount || 0,
            childCount: formStore.childCount || 0,
        });

        itineraryStore.initItineraryModule(
            this.reserveType === ReserveType.Bus
                ? {
                      adultCount: formStore.adultCount || 0,
                      childCount: formStore.childCount || 0,
                      infantCount: formStore.infantCount || 0,
                      origin: this.originCode || '',
                      destination: this.destinationCode || '',
                      departureDate: new Date(this.departureDate || ''),
                      originTerminalId: this.originCode?.toString(),
                      destinationTerminalId: this.destinationCode?.toString(),
                  }
                : new FlightUrlParams(
                      [
                          {
                              departureDate: new Date(this.departureDate || ''),
                              destination: this.destinationCode || '',
                              origin: this.originCode || '',
                          },
                      ],
                      formStore.adultCount || 0,
                      formStore.childCount || 0,
                      formStore.infantCount || 0,
                      'All'
                  )
        );
        await currentModule.value.loadAvailable({ index: 0 });
        this.initLoading = false;
    }

    get fullySelectedPeriods() {
        return this.mapSelectedPeriods(
            this.routeDemand?.departureSplitTimesPerDays || []
        );
    }

    get mapedSelectedPeriod() {
        return this.customTimes
            ? [
                  {
                      label: 'بازه زمانی دلخواه',
                      subLabel: `${this.customTimes?.from?.split(':')[0] || 0} الی ${this.customTimes?.to?.split(':')[0] || 0}`,
                      icon: faClock,
                      items: [
                          {
                              label: `${this.customTimes?.from} تا ${this.customTimes?.to}`,
                              value: {
                                  from: this.customTimes?.from,
                                  to: this.customTimes?.to,
                              },
                          },
                      ],
                  },
              ]
            : [];
    }

    get customSelectedPeriods() {
        return this.mapSelectedCustomPeriods(
            this.routeDemand?.departureSplitTimesPerDays || []
        );
    }

    get haveFullySelectedDay() {
        return this.fullySelectedPeriods.length === 4;
    }

    get hasFilter() {
        return (
            !!this.selectedCarrierCodes.length ||
            !!this.selectedVehicleTypes.length ||
            this.selectedSeatType !== SeatTypes.NUMBER_1 ||
            this.routeDemand.baggageType !== BaggageTypes.NUMBER_1 ||
            !!this.originCode ||
            !!this.destinationCode ||
            this.classOfService.value !== classOfServiceEnumMap[0].value
        );
    }

    get interceptionItems(): SelectItemType<number>[] {
        const result: SelectItemType<number>[] = [];
        if (
            this.prices?.alternativeMinimumTimesToDeparture === null ||
            this.prices?.alternativeMinimumTimesToDeparture === undefined
        ) {
            return result;
        }
        return this.prices.alternativeMinimumTimesToDeparture.map(time => ({
            id: time.minimumDepartureTimeInHour || 0,
            label: time.minimumDepartureTimeInHour
                ? `${time.minimumDepartureTimeInHour} ساعت قبل از ${this.reserveTypeInfo.run}`
                : 'لحظه حرکت',
            checked: time.isDefault,
        }));
    }

    toggleAutoReservePackage({
        reservePackage,
        active,
    }: {
        reservePackage: ItineraryPackage;
        active: boolean;
    }) {
        if (active) {
            this.selectedPackages = [...this.selectedPackages, reservePackage];
        } else {
            this.selectedPackages = this.selectedPackages.filter(
                itineraryPackage =>
                    !EnumClass.create(
                        reservePackage.autoReserveDemandParams
                    ).isEqual(itineraryPackage.autoReserveDemandParams)
            );
        }
    }

    get servicePriceDiscount() {
        if (this.applyUserScoreDiscount) {
            return 0;
        } else {
            return this.prices?.servicePriceDiscount;
        }
    }

    get currentReserveMinPrice() {
        return this.prices?.minPrice || 0;
    }

    private get routeInfoDate() {
        const dates =
            this.routeDemand.departureSplitTimesPerDays
                ?.filter(demand => demand.date)
                .map(demand => new Date(demand.date ?? '')) ?? [];
        return formatDateList(...dates);
    }

    private get itemInfoDate() {
        const formStore = useFormStore();
        const itineraryStore = useItineraryStore();
        const [selectedAutoReservePackage] = this.selectedPackages;
        const currentModule = useCurrentItineraryModule();
        const segment = (
            (selectedAutoReservePackage ??
                formStore.emptyPackage ??
                itineraryStore.emptyPackage ??
                currentModule.value.autoReservableList?.[0]) as TripPackage
        )?.segments?.[0];
        const trip = segment?.digest?.[0];
        return dayConfig.js(trip?.departureDateTime).format('DD MMMM');
    }

    get infoDate() {
        switch (this.autoReserveType) {
            case AutoReserveType.Item:
                return this.itemInfoDate;
            case AutoReserveType.Route:
                return this.routeInfoDate;
            default:
                return '';
        }
    }

    get autoReserveBillEntries(): ItineraryPackageInvoiceEntries[] {
        if (!this.prices) {
            return [];
        }
        const formStore = useFormStore();
        return [
            {
                prices: [
                    {
                        price: this.currentSelectedFlightPrice?.adultPrice || 0,
                        paxType: PaxType.Adult,
                        title: 'بزرگسال',
                        count: formStore.adultCount,
                    },
                    {
                        price: this.currentSelectedFlightPrice?.childPrice || 0,
                        paxType: PaxType.Child,
                        title: 'کودک',
                        count: formStore.childCount,
                    },
                    {
                        price:
                            this.currentSelectedFlightPrice?.infantPrice || 0,
                        paxType: PaxType.Infant,
                        title: 'نوزاد',
                        count: formStore.infantCount || 0,
                    },
                    {
                        price:
                            this.applyUserScoreDiscount ||
                            this.isUserScoreDiscountOnBillActive
                                ? 0
                                : this.prices.servicePrice || 0,
                        paxType: PaxType.Adult,
                        title: 'هزینه رزرو خودکار',
                        count: 1,
                        discount: this.servicePriceDiscount,
                    },
                ].filter(price => price.count),
            },
        ];
    }

    async applyUserScoreDiscountOnBill() {
        try {
            if (!this.isUserScoreDiscountOnBillActive) {
                const {
                    data: { serviceDiscount },
                } =
                    await AutomaticReserveApiApiAutomaticReserveAddUserScoreDiscountPost(
                        {
                            userScoreDiscountRequestDto: {
                                automaticReserveDemandId:
                                    this.reserveResponse
                                        ?.automaticReserveDemandId || 0,
                                giftMacValue: {
                                    mac: this.giftMac,
                                    giftCode: this.giftCode,
                                },
                            },
                        }
                    );
                if (this.prices) {
                    this.prices.servicePriceDiscount = serviceDiscount;
                }
            } else {
                await AutomaticReserveApiApiAutomaticReserveRemoveUserScoreDiscountPost(
                    {
                        userScoreDiscountRequestDto: {
                            automaticReserveDemandId:
                                this.reserveResponse
                                    ?.automaticReserveDemandId || 0,
                            giftMacValue: {
                                mac: this.giftMac,
                                giftCode: this.giftCode,
                            },
                        },
                    }
                );
                if (this.prices) {
                    this.prices.servicePriceDiscount = undefined;
                }
            }
            this.isUserScoreDiscountOnBillActive =
                !this.isUserScoreDiscountOnBillActive;
        } catch {}
    }

    resetAutoReserve(clearAll = false) {
        // auto-reserve reset:
        this.autoReserveSuccessRate = 0;
        this.useCredit = true;
        this.customTimes = null;
        this.isLoadingCreateReserve = false;
        this.prices = null;
        this.isLoadFromSession = false;
        this.reserveResponse = null;
        if (clearAll) {
            this.selectedPackages = [];
            this.preSelectPackage = null;
            this.showAll = false;
        }
        this.routeDemand = {
            carrierCodes: [],
            vehicleTypeCodes: [],
            baggageType: BaggageTypes.NUMBER_1,
            departureSplitTimesPerDays: [],
        };
        this.selectedSeatType = SeatTypes.NUMBER_1;
        sessionStorage.removeItem('autoReserveData');
        this.applyUserScoreDiscount = false;

        // external states
        const invoiceStore = useInvoiceStore();
        invoiceStore.informationRegisterStatus = InformationRegisterStatus.None;
        invoiceStore.pax = [];
        invoiceStore.isLoadFromSession = false;
        sessionStorage.removeItem('invoiceData');
        // invoiceStore: safe
        const formStore = useFormStore();
        formStore.itineraryPackages = [];
        formStore.emptyPackage = null;
        sessionStorage.removeItem('reserveData');
        formStore.isLoadFromSession = false;
    }

    get autoReserveAllItems(): {
        itineraryPackage: FlightPackage;
        selected: boolean;
        order?: number;
    }[] {
        const currentModule = useCurrentItineraryModule();
        const list: FlightPackage[] = [
            ...(currentModule.value.autoReservableList as FlightPackage[]),
            ...(currentModule.value.sortedReservableList as FlightPackage[]),
        ];
        return list
            .map((pkg, index) => ({
                itineraryPackage: pkg,
                selected: this.selectedPackages.some(selected =>
                    EnumClass.create(selected.autoReserveDemandParams).isEqual(
                        pkg.autoReserveDemandParams
                    )
                ),
                order: pkg === this.preSelectPackage ? -1 : index,
            }))
            .sort((a, b) => a.order - b.order);
    }

    get autoReserveCurrentEditItems(): {
        itineraryPackage: FlightPackage;
        selected: boolean;
        order?: number;
    }[] {
        const currentModule = useCurrentItineraryModule();
        const list: FlightPackage[] = [
            ...(currentModule.value.autoReservableList as FlightPackage[]),
            ...(currentModule.value.sortedReservableList.filter(reserve =>
                this.selectedPackages
                    .map(({ uniqueId }) => uniqueId)
                    .includes(reserve.uniqueId)
            ) as FlightPackage[]),
        ];

        return list
            .map((pkg, index) => ({
                itineraryPackage: pkg,
                selected: this.selectedPackages.some(selected =>
                    EnumClass.create(selected.autoReserveDemandParams).isEqual(
                        pkg.autoReserveDemandParams
                    )
                ),
                order: pkg === this.preSelectPackage ? -1 : index,
            }))
            .sort((a, b) => a.order - b.order);
    }

    async initAutoReserveEdit(id: number) {
        this.editingInitialLoading = true;
        const searchFormStore = useSearchFormStore();
        const itineraryStore = useItineraryStore();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const currentModule = useCurrentItineraryModule();
        const formStore = useFormStore();
        formStore.clearState();

        const { data } =
            await AutomaticReserveApiApiAutomaticReserveGetAutomaticReserveDetailsByIdGet(
                { id }
            );
        this.currentAutoReserveEditing = {
            ...data,
            routeDemand: {
                ...data.routeDemand,
                departureSplitTimesPerDays:
                    data.routeDemand?.departureSplitTimesPerDays?.map(item => ({
                        ...item,
                        timeRanges: item.timeRanges?.map(timeRange => ({
                            from: timeRange.from?.substring(0, 5),
                            to: timeRange.to?.substring(0, 5),
                        })),
                    })),
            },
            id,
        };
        searchFormStore.setReserveType(
            data?.serviceType === 2 ? ReserveType.Bus : ReserveType.Flight
        );
        currentSearchForm.value.loadViewUrlParams({
            destination: data?.destinationCode || '',
            origin: data?.originCode || '',
            departureDate: new Date(data?.departureDate || ''),
            adultCount: data?.adultCount || 0,
            infantCount: data?.infantCount || 0,
            childCount: data?.childCount || 0,
        });

        itineraryStore.initItineraryModule(
            data?.serviceType === 2
                ? {
                      adultCount: data?.adultCount || 0,
                      childCount: data?.childCount || 0,
                      infantCount: data?.infantCount || 0,
                      origin: data?.originCode || '',
                      destination: data?.destinationCode || '',
                      departureDate: new Date(data?.departureDate || ''),
                      originTerminalId: data.originCode?.toString(),
                      destinationTerminalId: data.destinationCode?.toString(),
                  }
                : new FlightUrlParams(
                      [
                          {
                              departureDate: new Date(
                                  data?.departureDate || ''
                              ),
                              destination: data?.destinationCode || '',
                              origin: data?.originCode || '',
                          },
                      ],
                      data?.adultCount || 0,
                      data?.childCount || 0,
                      data?.infantCount || 0,
                      'All'
                  )
        );
        await currentModule.value.loadAvailable({ index: 0 });
        this.routeDemand =
            JSON.parse(
                JSON.stringify(
                    this.currentAutoReserveEditing?.routeDemand || null
                )
            ) || this.routeDemand;
        this.classOfService =
            classOfServiceEnumMap.find(
                ({ value }) => value === data.classOfService
            ) || classOfServiceEnumMap[0];
        this.selectedPackages = this.autoReserveAllItems
            .filter(({ itineraryPackage: { segments } }) =>
                segments.some(({ trips }) =>
                    trips.some(({ serviceId }) =>
                        data?.items
                            ?.map(({ serviceId }) => serviceId)
                            .includes(serviceId.toString())
                    )
                )
            )
            .map(({ itineraryPackage }) => itineraryPackage);

        useAuthStore().giftsSetter({
            mac: data?.giftMac?.mac || '',
            code: data?.giftMac?.giftCode || '',
        });
        this.departureDate = data?.departureDate || '';
        // this.routeDemand = [
        //     {
        //         departureDate: new Date(data?.departureDate || ''),
        //         destination: data?.destinationCode || '',
        //         origin: data?.originCode || '',
        //     },
        // ];
        this.selectedSeatType = data?.seatType || this.selectedSeatType;
        this.interceptionThresholdInHour =
            data?.interceptionThresholdInHour ||
            this.interceptionThresholdInHour;
        this.selectedDelay =
            data.departureTimeDelayedThreshold || this.selectedDelay;
        this.selectedEarly =
            data.departureTimeEarlyThreshold || this.selectedEarly;
        // use later for pricing actions
        // autoReserveStore.getAutoReservePrice();
        this.currentSelectedFlightPrice = {
            route: {
                to: data?.destinationCode,
                from: data?.originCode,
            },
            adultPrice: data?.adultPrice || 0,
            childPrice: data?.childPrice || 0,
            infantPrice: data?.infantPrice || 0,
        };
        formStore.setCounts({
            adultCount: data?.adultCount || 0,
            childCount: data?.childCount || 0,
            infantCount: data?.infantCount || 0,
        });
        this.interceptionThresholdInHour =
            data?.interceptionThresholdInHour || 3;

        this.editingInitialLoading = false;
    }

    get billCode(): string | null {
        return this.reserveResponse?.billCode || null;
    }

    get billId(): number | null {
        return this.reserveResponse?.billId || null;
    }

    get paymentAmount(): number | null {
        return this.reserveResponse?.shouldPayPrice ?? null;
    }

    get trips(): [Trip, TripPriceClass | null][] {
        return this.selectedPackages
            .filter<TripPackage>(ItineraryPackage.isTripPackage)
            .flatMap(pkg => {
                if (pkg.isFlightPackage()) {
                    pkg.selectedPrices = [
                        new FlightPriceClass(
                            {
                                BookingClass:
                                    this.classOfService.cabinClass === 'All'
                                        ? 'همه‌ی کلاس‌های نرخی'
                                        : this.classOfService.title,
                                Capacity: 0,
                                CabinClass: this.classOfService.cabinClass,
                            },
                            pkg.international,
                            undefined
                        ),
                    ];
                }
                return pkg.segments
                    .reduce(
                        (acc, segment) => acc.concat(segment.trips),
                        <Trip[]>[]
                    )
                    .map(
                        t =>
                            [t, pkg?.selectedPrice || null] as [
                                Trip,
                                TripPriceClass | null,
                            ]
                    );
            });
    }

    private mapDemandResponseToPackage(
        demandResponse: AutomaticReserveDemandResponse
    ): TripPackage[] {
        return (
            demandResponse.items?.map(demand => {
                switch (demand.reserveType) {
                    case ServiceType.NUMBER_0:
                        return new FlightPackage(
                            {
                                Prices: [
                                    {
                                        Capacity: 0,
                                    },
                                ],
                                Segments: [
                                    {
                                        Legs: [
                                            {
                                                ArrivalTime: demand.arrivalTime,
                                                DepartureTime:
                                                    demand.departureTime,
                                                Origin: demandResponse.origin,
                                                Destination:
                                                    demandResponse.destination,
                                                Airline: {
                                                    PersianTitle:
                                                        demand.providerName,
                                                    IataCode: demand.providerId,
                                                },
                                                FlightNumber:
                                                    demand.serviceNumber,
                                            },
                                        ],
                                    },
                                ],
                            },
                            null,
                            {}
                        );
                    case ServiceType.NUMBER_2:
                        return new BusPackage(
                            {
                                id: demandResponse.id,
                                from: Number(demandResponse.originCode),
                                to: Number(demandResponse.destinationCode),
                                fromName: demandResponse.origin,
                                toName: demandResponse.destination,
                                fromCity: demandResponse.origin,
                                toCity: demandResponse.destination,
                                departureTime: demand.departureTime,
                                arrivalTime: demand.arrivalTime,
                                capacity: 0,
                                superCorporationID: demand.providerId
                                    ? +demand.providerId
                                    : 0,
                                superCorporation: demand.providerName,
                            },
                            demandResponse.adultCount,
                            false,
                            ReserveType.Bus
                        );
                    default:
                        throw new Error('service type not supported');
                }
            }) || []
        );
    }

    get isAutoReserve(): boolean {
        return this.prices !== null;
    }

    async proceedPayment() {
        if (!this.reserveResponse) {
            return false;
        }
        function parseAndSave(url: string) {
            const [path, queryString] = url.split('?');
            const queries = parse(queryString);
            queries.accessToken = '[Redacted]';
            localStorage.setItem(
                'AutoReservePaymentUrl',
                `${path}?${stringify(queries)}`
            );
        }
        const { paymentUrl, optionalPaymentUrl, totalPricePaymentUrl } =
            this.reserveResponse;
        if (optionalPaymentUrl) {
            const { open } = usePopOver({
                component: OptionalPaymentModal,
                attrs: {
                    price: this.reserveResponse.optionalPayPrice ?? 0,
                },
            });
            const accepted = await open();
            if (accepted.args?.[0]) {
                parseAndSave(optionalPaymentUrl);
                window.location.href = optionalPaymentUrl;
                return true;
            }
        }
        if (totalPricePaymentUrl && !this.useCredit && !this.oldFlowEnabled) {
            parseAndSave(totalPricePaymentUrl);
            window.location.href = totalPricePaymentUrl;
        } else if (paymentUrl) {
            parseAndSave(paymentUrl);
            window.location.href = paymentUrl;
        }
        // if (paymentUrl) {
        //     parseAndSave(paymentUrl);
        //     window.location.href = paymentUrl;
        // }
        else {
            await AutomaticReserveApiApiAutomaticReserveSetInProcessAutomaticReserveIdPost(
                {
                    automaticReserveId:
                        this.reserveResponse.automaticReserveDemandId || 0,
                }
            );
            window.location.href = `/after-order/auto-reserve?paymentStatus=success&reserveId=${this.reserveResponse.automaticReserveDemandId}`;
        }
        return true;
    }

    async getPassengers(reserveId: number) {
        const { data } =
            await AutomaticReserveApiApiAutomaticReserveIdPeopleV2Get({
                id: reserveId,
            });
        return {
            people: data.people || [],
            contact: {
                mobile: data.mobile || '',
                email: data.email || '',
                phone: data.phone || '',
            },
        };
    }

    async removePassengers(
        params: AutomaticReserveApiApiAutomaticReserveIdPeopleDeleteRequest
    ) {
        await AutomaticReserveApiApiAutomaticReserveIdPeopleDelete(params);
    }

    async getCancellationReasons() {
        if (this.cancellationReasons.length) {
            return;
        }
        const { data } =
            await AutomaticReserveApiApiAutomaticReserveCancellationReasonsGet();
        this.cancellationReasons = data;
    }

    async getAutoReserveDemands(
        page: number,
        current = false,
        pageSize?: number
    ) {
        if (!useAuthStore().loggedIn) {
            return;
        }
        try {
            const { data } = await AutomaticReserveApiApiAutomaticReserveGet({
                pageNumber: page,
                pageSize: pageSize || 20,
                current,
            });
            if (page === 1) {
                this.demands = [];
            }
            this.chargeNeededForAutoReserves =
                data.chargePendingAutoReservesTotalPrice;
            this.chargePendingAutoReservesPaymentLink =
                data.chargePendingAutoReservesPaymentLink;
            this.demands = [...this.demands, ...(data.list || [])];
            this.demandsCount = data.count || 0;
        } catch (e) {
            reportException(e);
        }
    }

    async cancelDemand(
        params: AutomaticReserveApiApiAutomaticReserveCancelAutomaticReserveDemandIdPatchRequest
    ) {
        try {
            await AutomaticReserveApiApiAutomaticReserveCancelAutomaticReserveDemandIdPatch(
                params
            );
        } catch (error) {
            if (isAxiosError(error)) {
                throw new Error(error.response?.data.Message);
            } else {
                throw new Error('خطایی رخ داد!!!');
            }
        }
    }

    async getAutoReservePrice() {
        this.isLoadingPrices = true;
        const searchFormStore = useSearchFormStore();
        const currentModule = useCurrentItineraryModule<TripModule>();
        const { data } = await AutomaticReserveApiApiAutomaticReservePriceGet({
            origin: currentModule.value.originCurrentCode,
            destination: currentModule.value.destinationCurrentCode,
            giftMacValueGiftCode: this.giftCode || undefined,
            giftMacValueMac: this.giftMac || undefined,
            date: this.departureDate || undefined,
            serviceType:
                searchFormStore.reserveType === ReserveType.Bus ? 2 : 0,
        });
        this.prices = data;
        this.interceptionThresholdInHour = 3;
        this.selectedEarly = 30;
        this.selectedDelay = 30;
        this.interceptionThresholdInHour =
            data.alternativeMinimumTimesToDeparture?.find(
                time => time.isDefault
            )?.minimumDepartureTimeInHour || 0;
        if (data.canRequestExtraPayment) {
            this.flightPrices = [
                ...(data.flightPrices ?? []),
                {
                    title: 'رزرو خودکار تا مبلغ دلخواه',
                    adultPrice: data.flightPrices?.length
                        ? 0
                        : data.adultPrice || 0,
                    childPrice: data.flightPrices?.length
                        ? 0
                        : data.childPrice || 0,
                    infantPrice: data.flightPrices?.length
                        ? 0
                        : data.infantPrice || 0,
                    isCustomPrice: true,
                    description:
                        'بلیط‌های تا مبلغ دلخواه شما را خرید خواهیم کرد.',
                },
            ];
        } else {
            this.flightPrices = [
                {
                    adultPrice:
                        data.adultPrice === null ? undefined : data.adultPrice,
                    childPrice:
                        data.childPrice === null ? undefined : data.childPrice,
                    infantPrice:
                        data.infantPrice === null
                            ? undefined
                            : data.infantPrice,
                    recommended: true,
                },
            ];
        }
        this.currentSelectedFlightPrice =
            this.flightPrices.find(p => p.recommended) ?? this.flightPrices[0];
        this.isLoadingPrices = false;
    }

    async getAutoReservePaymentLink(id: number) {
        const { data } =
            await AutomaticReserveApiApiAutomaticReserveIdPaymentGet({
                id,
            });
        return data.paymentUrl || '';
    }

    async editAutoReserve(
        editAutomaticReserveDemandRequestDto: EditAutomaticReserveDemandRequestDto
    ) {
        const { data } =
            await AutomaticReserveApiApiAutomaticReserveEditAutomaticReserveDemandPost(
                {
                    editAutomaticReserveDemandRequestDto,
                }
            );
        return data;
    }

    async editAutoReservePeople(
        registerAutomaticReservePeopleRequestDto: RegisterAutomaticReservePeopleRequestDto
    ) {
        await AutomaticReserveApiApiAutomaticReserveRegisterAutomaticReservePeoplePost(
            {
                registerAutomaticReservePeopleRequestDto,
            }
        );
    }

    saveSession() {
        const currentModule = useCurrentItineraryModule<TripModule>();
        const formStore = useFormStore();
        const data = JSON.stringify({
            prices: this.prices,
            reserveResponse: this.reserveResponse,
            showAll: this.showAll,
            customTimes: this.customTimes,
            isUserScoreDiscountOnBillActive:
                this.isUserScoreDiscountOnBillActive,
            classOfService: this.classOfService,
            autoReserveType: this.autoReserveType,
            adultCount: formStore.adultCount || 0,
            childCount: formStore.childCount || 0,
            infantCount: formStore.infantCount || 0,
            request: {
                items: this.selectedPackages,
                currentSelectedFlightPrice: this.currentSelectedFlightPrice,
                originCode:
                    this.originCode ||
                    currentModule.value.originCurrentCode ||
                    '',
                destinationCode:
                    this.destinationCode ||
                    currentModule.value.destinationCurrentCode ||
                    '',
                giftCode: this.giftCode,
                giftMac: this.giftMac,
                classOfService: this.classOfService.value,
                departureDate: this.departureDate,
                routeDemand: this.routeDemand,
                seatType: this.selectedSeatType,
                interceptionThresholdInHour: this.interceptionThresholdInHour,
                departureTimeDelayedThreshold: this.selectedDelay,
                departureTimeEarlyThreshold: this.selectedEarly,
                applyUserScoreDiscount: this.applyUserScoreDiscount,
            },
        });
        sessionStorage.setItem('autoReserveData', data);
    }

    get oldFlowEnabled() {
        return (
            (usePosthog().value?.getFeatureFlag('auto-new-modal-test') ||
                'old-modal') === 'old-modal'
        );
    }

    get defaultRequestType(): AutoReserveType {
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        return itineraryStore.getItineraryModule(searchFormStore.reserveType)
            .defaultAutoReserveType;
    }

    get reserveType() {
        return `auto-reserve-${useSearchFormStore().reserveType}-${this.autoReserveType}`;
    }

    get reserveTypeInfo() {
        switch (useSearchFormStore().reserveType) {
            case ReserveType.Flight:
                return {
                    carrier: 'هواپیمایی',
                    vehicle: 'هواپیما',
                    run: 'پرواز',
                    station: 'فرودگاه',
                    ticketsOf: 'پروازهای',
                    tickets: 'پروازها',
                    reserve: 'پرواز',
                    indicator: 'پروازهای',
                    reserveOf: 'پروازی',
                };
            case ReserveType.Bus:
                return {
                    carrier: 'مسافربری',
                    vehicle: 'اتوبوس',
                    run: 'حرکت اتوبوس',
                    station: 'ترمینال',
                    ticketsOf: 'بلیط‌های اتوبوس',
                    tickets: 'بلیط‌ها',
                    reserve: 'اتوبوس',
                    indicator: 'اتوبوس های',
                    reserveOf: 'اتوبوسی',
                };
            default:
                return {};
        }
    }

    clearFilters() {
        this.clearVehicleTypes();
        this.clearCarrierCodes();
        this.selectedSeatType = SeatTypes.NUMBER_1;
        this.routeDemand.baggageType = BaggageTypes.NUMBER_1;
        this.originCode = '';
        this.destinationCode = '';
        [this.classOfService] = classOfServiceEnumMap;
    }

    //#region vehicle-types
    get selectedVehicleTypes() {
        return (
            this.routeDemand.vehicleTypeCodes?.map(({ code }) => code || '') ||
            []
        );
    }

    toggleVehicleType(code: string, checked: boolean) {
        if (checked) {
            this.routeDemand.vehicleTypeCodes?.push({ code });
        } else {
            const index =
                this.routeDemand.vehicleTypeCodes?.findIndex(
                    vehicle => vehicle.code === code
                ) ?? -1;
            if (index !== -1) {
                this.routeDemand.vehicleTypeCodes?.splice(index, 1);
            }
        }
    }

    clearVehicleTypes() {
        this.routeDemand.vehicleTypeCodes = [];
    }
    //#endregion

    //#region carrier-codes
    get selectedCarrierCodes() {
        return (
            this.routeDemand.carrierCodes?.map(({ code }) => code || '') || []
        );
    }

    toggleCarrierCode(code: string, checked: boolean) {
        if (checked) {
            this.routeDemand.carrierCodes?.push({ code });
        } else {
            const index =
                this.routeDemand.carrierCodes?.findIndex(
                    carrier => carrier.code === code
                ) ?? -1;
            if (index !== -1) {
                this.routeDemand.carrierCodes?.splice(index, 1);
            }
        }
    }

    clearCarrierCodes() {
        this.routeDemand.carrierCodes! = [];
    }
    //#endregion

    async getReserveInfo(id: number) {
        const { data } = await AutomaticReserveApiApiAutomaticReserveIdGet({
            id,
        });
        return data;
    }

    async getAutoReserveRate() {
        const pkg = this.preSelectPackage as TripPackage;
        const currentModule = useCurrentItineraryModule<TripModule>();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const searchFormStore = useSearchFormStore();
        const serviceType =
            searchFormStore.reserveType === ReserveType.Bus ? 2 : 0;
        const paxCount =
            currentSearchForm.value.adultCount +
            currentSearchForm.value.childCount +
            currentSearchForm.value.infantCount;
        try {
            if (pkg) {
                const { data: itemRate } =
                    await AutomaticReserveApiApiAutomaticReserveCalculateAutomaticReserveDemandItemSuccessRatePost(
                        {
                            calculateAutomaticReserveDemandItemSuccessRateRequestDto:
                                {
                                    serviceType,
                                    providerId:
                                        pkg.autoReserveDemandParams.providerId,
                                    providerName:
                                        pkg.autoReserveDemandParams
                                            .providerName,
                                    departureTime: dayConfig
                                        .gregory(pkg.departureDateTime)
                                        .toISOString(),
                                    arrivalTime: dayConfig
                                        .gregory(pkg.arrivalDateTime)
                                        .toISOString(),
                                    serviceNumber: pkg.serviceNumber,
                                    serviceId: pkg.serviceId.toString(),
                                    destinationCode: pkg.destinationCode,
                                    userPopupViewerCount: paxCount,
                                    originCode: pkg.originCode,
                                },
                        }
                    );
                return itemRate;
            } else {
                const { data: routeRate } =
                    await AutomaticReserveApiApiAutomaticReserveCalculateAutomaticReserveRouteDemandItemSuccessRatePost(
                        {
                            calculateAutomaticReserveRouteDemandItemSuccessRateRequestDto:
                                {
                                    originCode:
                                        currentModule.value.originCurrentCode,
                                    destinationCode:
                                        currentModule.value
                                            .destinationCurrentCode,
                                    serviceType,
                                    departureDate: dayConfig
                                        .gregory(
                                            currentSearchForm.value
                                                .departureDate ||
                                                this.departureDate
                                        )
                                        .toISOString(),
                                    userPopupViewerCount: paxCount,
                                },
                        }
                    );
                return routeRate;
            }
        } catch (e) {
            await handleException(e);
            return 0;
        }
    }

    async loadAutoReserveRate() {
        this.autoReserveSuccessRate = await this.getAutoReserveRate();
    }

    loadSession() {
        if (this.prices) {
            return;
        }
        const dataStr = sessionStorage.getItem('autoReserveData');
        if (!dataStr) {
            return;
        }
        const formStore = useFormStore();
        const dataObj = JSON.parse(dataStr);
        if (dataObj.reserveResponse) {
            this.reserveResponse = dataObj.reserveResponse;
        }
        this.autoReserveType = dataObj.autoReserveType;
        this.prices = dataObj.prices;
        this.classOfService = dataObj.classOfService;
        this.selectedPackages = dataObj.request.items.map(
            (item: ItineraryPackage) => ItineraryModule.revivePackage(item)
        );
        this.customTimes = dataObj.customTimes;
        this.showAll = !!dataObj.showAll;
        useAuthStore().giftsSetter({
            mac: dataObj.request.giftMac,
            code: dataObj.request.giftCode,
        });
        this.isUserScoreDiscountOnBillActive =
            dataObj.isUserScoreDiscountOnBillActive;
        this.departureDate = dataObj.request.departureDate;
        this.originCode = dataObj.request.originCode;
        this.interceptionThresholdInHour =
            dataObj.request.interceptionThresholdInHour;
        this.selectedDelay = dataObj.request.departureTimeDelayedThreshold;
        this.selectedEarly = dataObj.request.departureTimeEarlyThreshold;
        this.destinationCode = dataObj.request.destinationCode;
        this.currentSelectedFlightPrice =
            dataObj.request.currentSelectedFlightPrice;
        this.routeDemand = dataObj.request.routeDemand || {
            carrierCodes: [],
            vehicleTypeCodes: [],
            departureSplitTimesPerDays: [],
            baggageType: BaggageTypes.NUMBER_1,
        };
        this.selectedSeatType = dataObj.request.seatType;
        this.applyUserScoreDiscount = dataObj.request.applyUserScoreDiscount;
        formStore.setItineraryPackagesAndDepartureTime([
            this.selectedPackages[0],
        ]);
        formStore.setCounts({
            adultCount: dataObj.adultCount || 0,
            childCount: dataObj.childCount || 0,
            infantCount: dataObj.infantCount || 0,
        });
        this.isLoadFromSession = true;
    }

    get eventMetric() {
        const searchFormStore = useSearchFormStore();
        const authStore = useAuthStore();

        return {
            autoReserveType: `${searchFormStore.reserveType}${this.showAll ? 'ARPrice' : 'ARCap'}`,
            priceCeiling: this.prices?.adultPrice,
            reserveMode: this.selectedPackages.length ? 'service' : 'route',
            cheapOrCapacity: this.autoReserveType,
            selectedItemsCount: this.selectedPackages.length,
            selectedService: this.selectedPackages
                .map(pkg => pkg.uniqueId)
                .join(' - '),
            reserveDeadLine: this.interceptionThresholdInHour,
            customFiltersAdded: this.classOfService.value ? 1 : 0,
            enoughCredit: (this.paymentAmount || 0) < authStore.credit ? 1 : 0,
            vpn: authStore.vpn,
        };
    }

    reserveInfo(): AutomaticReserveDemandRequest {
        const formStore = useFormStore();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const currentModule = useCurrentItineraryModule<TripModule>();
        const searchFormStore = useSearchFormStore();
        const departureDate = dayConfig
            .gregory(
                currentSearchForm.value.departureDate || this.departureDate
            )
            .format(formatter());
        return {
            items: this.selectedPackages.length
                ? this.selectedPackages.map(pkg => pkg.autoReserveDemandParams)
                : null,
            routeDemand: this.routeDemand.departureSplitTimesPerDays?.length
                ? {
                      ...this.routeDemand,
                      departureSplitTimesPerDays:
                          this.routeDemand.departureSplitTimesPerDays.map(
                              day => ({
                                  ...day,
                                  timeRanges: [
                                      ...(day.timeRanges || []),
                                      ...(this.customTimes
                                          ? [this.customTimes]
                                          : []),
                                  ],
                              })
                          ),
                  }
                : undefined,
            seatType: this.selectedSeatType,
            adultCount: formStore.adultCount,
            childCount: formStore.childCount,
            infantCount: formStore.infantCount,
            classOfService: this.classOfService.value,
            originCode:
                this.originCode || currentModule.value.originCurrentCode,
            departureDate: departureDate || undefined,
            destinationCode:
                this.destinationCode ||
                currentModule.value.destinationCurrentCode,
            adultPrice: this.currentSelectedFlightPrice?.adultPrice,
            childPrice: this.currentSelectedFlightPrice?.childPrice,
            interceptionThresholdInHour: this.interceptionThresholdInHour,
            infantPrice: this.currentSelectedFlightPrice?.infantPrice,
            departureTimeEarlyThreshold: this.selectedEarly,
            departureTimeDelayedThreshold: this.selectedDelay,
            applyUserScoreDiscount: this.applyUserScoreDiscount,
            autoReserveType: this.showAll
                ? AutoReserveTypes.NUMBER_1
                : AutoReserveTypes.NUMBER_0,
            serviceType:
                searchFormStore.reserveType === ReserveType.Bus ? 2 : 0,
            giftMac:
                this.giftMac && this.giftCode
                    ? {
                          giftCode: this.giftCode,
                          mac: this.giftMac,
                      }
                    : undefined,
        };
    }

    handleEvents(
        step: string,
        url: string,
        status: 'success' | 'error',
        error?: any
    ) {
        const analyticStore = useAnalyticsStore();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const invoiceStore = useInvoiceStore();
        const authStore = useAuthStore();
        analyticStore.trackAll(`auto-reserve-${status}`, {
            step,
            error,
            creationSource: authStore.siteSourceId,
            tripType: currentSearchForm.value?.tripType,
            link: url,
            from: currentSearchForm.value?.origin?.title,
            to: currentSearchForm.value.destination?.title,
            price: invoiceStore.totalPrice,
            reserveType: this.reserveType,
            ...this.reserveInfo(),
        });
    }

    async checkReserve() {
        try {
            const { data } =
                await AutomaticReserveApiApiAutomaticReserveGetAutomicReserveDemandItemsAvailabilityPost(
                    {
                        automaticReserveDemandRequest: this.reserveInfo(),
                    }
                );
            this.demandAvailabilityCheck = data;
        } catch (e) {
            reportException(e);
        }
    }

    async createReserve() {
        if (
            !this.selectedPackages.length &&
            !(
                this.routeDemand.departureSplitTimesPerDays?.length &&
                this.routeDemand.departureSplitTimesPerDays.every(
                    item => item.date && item.timeRanges?.length
                )
            )
        ) {
            return;
        }
        const invoiceStore = useInvoiceStore();
        const formStore = useFormStore();
        this.isLoadingCreateReserve = true;
        try {
            const { data } = await AutomaticReserveApiApiAutomaticReservePost({
                automaticReserveDemandRequest: this.reserveInfo(),
            });
            if (data.billCode) {
                formStore.handleUtmCampaigns(data.billCode);
            }
            this.reserveResponse = data;
            invoiceStore.billCode = data.billCode ?? null;
            invoiceStore.billId = data.billId ?? null;
            this.saveSession();
        } finally {
            this.isLoadingCreateReserve = false;
        }
    }
}

export const {
    use: useAutoReserveStore,
    useIfInstalled: useAutoReserveStoreIfInstalled,
} = defineClassStore('auto-reserve', () => new AutoReserveStore());
